import React, { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { LogOut, Inbox, Filter, Mail, Phone, User, Home, CheckCircle2, Search } from "lucide-react";
import { adminApi, clearAdminToken, getAdminToken, setAdminToken, API } from "../lib/api";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

function LoginView() {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await adminApi.post("/admin/login", { password: pwd });
      setAdminToken(data.access_token);
      toast.success("Welcome back.");
      window.location.replace("/admin/inbox");
    } catch {
      toast.error("Incorrect password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4" data-testid="admin-login">
      <form onSubmit={submit} className="alpha-card p-8 w-full max-w-md">
        <img src="/brand/logo-green-mark.png" alt="Alpha Community Care" className="h-12 w-auto" />
        <h1 className="mt-5 font-display text-2xl font-semibold">Admin sign-in</h1>
        <p className="text-sm text-[color:var(--alpha-mute)] mt-1">Submissions inbox for the Alpha Community Care team.</p>
        <label className="text-sm font-semibold mt-6 block">Password</label>
        <Input type="password" className="mt-2 h-11" value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus data-testid="admin-password" />
        <button disabled={loading || !pwd} className="alpha-btn-primary mt-5 w-full justify-center" data-testid="admin-login-submit">
          {loading ? "Signing in…" : "Sign in"}
        </button>
        <Link to="/" className="mt-4 block text-center text-xs text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-purple-700)]">← Back to site</Link>
      </form>
    </div>
  );
}

const KIND_LABEL = {
  contact: "Contact",
  participant_referral: "Participant referral",
  coordinator_referral: "Coordinator referral",
  vacancy_enquiry: "Vacancy enquiry",
};
const KIND_ICON = {
  contact: Mail,
  participant_referral: User,
  coordinator_referral: User,
  vacancy_enquiry: Home,
};

function InboxView() {
  const [data, setData] = useState({ items: [], totals: {}, unread: {} });
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (kind !== "all") params.kind = kind;
      if (status !== "all") params.status = status;
      const { data } = await adminApi.get("/admin/submissions", { params });
      setData(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAdminToken();
        nav("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, status]);

  const logout = () => {
    clearAdminToken();
    nav("/admin");
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await adminApi.patch(`/admin/submissions/${id}`, { status: newStatus });
      toast.success("Updated");
      setActive((a) => (a && a.id === id ? { ...a, status: newStatus } : a));
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (kind !== "all") params.set("kind", kind);
      if (status !== "all") params.set("status", status);
      const res = await adminApi.get(`/admin/submissions.csv?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `alpha-submissions-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const filtered = data.items.filter((s) => {
    if (!q) return true;
    const blob = JSON.stringify(s).toLowerCase();
    return blob.includes(q.toLowerCase());
  });

  const totalUnread = Object.values(data.unread).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-[color:var(--alpha-soft)]" data-testid="admin-inbox">
      <div className="bg-white border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-green-mark.png" alt="Alpha" className="h-9 w-auto" />
            <div>
              <div className="font-display font-semibold">Admin · Submissions inbox</div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-[color:var(--alpha-mute)]">{totalUnread} unread</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={exportCsv} className="alpha-btn-ghost text-sm !py-2 !px-4" data-testid="admin-export-csv">
              Export CSV
            </button>
            <Link to="/" className="alpha-btn-ghost text-sm">View site</Link>
            <button onClick={logout} className="text-sm font-semibold text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-purple-700)] flex items-center gap-1"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </div>

      <div className="alpha-container py-8 grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 xl:col-span-3 space-y-4">
          <div className="alpha-card p-5">
            <div className="alpha-overline flex items-center gap-2"><Filter className="w-3.5 h-3.5" /> Filter</div>
            <div className="mt-4 space-y-3">
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="h-10" data-testid="admin-filter-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="contact">Contact</SelectItem>
                  <SelectItem value="participant_referral">Participant referral</SelectItem>
                  <SelectItem value="coordinator_referral">Coordinator referral</SelectItem>
                  <SelectItem value="vacancy_enquiry">Vacancy enquiry</SelectItem>
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-10" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--alpha-mute)]" />
                <Input className="pl-9 h-10" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email…" data-testid="admin-search" />
              </div>
            </div>
          </div>
          <div className="alpha-card p-5">
            <div className="alpha-overline">Overview</div>
            <ul className="mt-3 space-y-2 text-sm">
              {Object.entries(KIND_LABEL).map(([k, label]) => (
                <li key={k} className="flex items-center justify-between">
                  <span>{label}</span>
                  <span className="alpha-chip">{data.totals[k] || 0}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <section className="lg:col-span-8 xl:col-span-9">
          <div className="alpha-card p-0 overflow-hidden">
            <div className="p-5 border-b border-[color:var(--alpha-line)] flex items-center gap-2">
              <Inbox className="w-4 h-4" /> <span className="font-display font-semibold">Submissions</span>
              <span className="ml-auto text-xs text-[color:var(--alpha-mute)]">{loading ? "Loading…" : `${filtered.length} items`}</span>
            </div>
            <ul className="divide-y divide-[color:var(--alpha-line)] max-h-[70vh] overflow-y-auto">
              {filtered.length === 0 && !loading && (
                <li className="p-10 text-center text-sm text-[color:var(--alpha-mute)]">No submissions match.</li>
              )}
              {filtered.map((s) => {
                const Icon = KIND_ICON[s.kind] || Mail;
                const who = s.data.full_name || s.data.name || s.data.coordinator_name || "Unknown";
                const email = s.data.email;
                return (
                  <li key={s.id}>
                    <button
                      onClick={() => setActive(s)}
                      className={`w-full text-left p-4 hover:bg-[color:var(--alpha-soft)] transition-colors flex items-start gap-3 ${active?.id === s.id ? "bg-[#EAF4FB]" : ""}`}
                      data-testid={`admin-row-${s.id}`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.status === "new" ? "alpha-gradient text-white" : "bg-[color:var(--alpha-soft)] text-[color:var(--alpha-purple-700)]"}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold truncate">{who}</div>
                          {s.status === "new" && <span className="alpha-chip !bg-[#E6F4EC] !text-[#3F8F5D] !border-[#CCE5D6]">New</span>}
                          {s.status === "contacted" && <span className="alpha-chip !bg-[color:var(--alpha-purple-100)] !text-[color:var(--alpha-purple-700)]">Contacted</span>}
                          {s.status === "converted" && <span className="alpha-chip !bg-[#DCEEE2] !text-[color:var(--alpha-green-700)] !border-[#CCE5D6]">Converted</span>}
                        </div>
                        <div className="text-xs text-[color:var(--alpha-mute)] truncate">{KIND_LABEL[s.kind]} · {email || "no email"}</div>
                      </div>
                      <div className="text-[11px] text-[color:var(--alpha-mute)] whitespace-nowrap">{new Date(s.created_at).toLocaleDateString()}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {active && (
            <div className="alpha-card p-6 mt-5" data-testid="admin-active-detail">
              <div className="flex items-center justify-between">
                <div>
                  <div className="alpha-overline">{KIND_LABEL[active.kind]}</div>
                  <div className="font-display text-lg font-semibold mt-1">{active.data.full_name || active.data.name || active.data.coordinator_name}</div>
                  <div className="text-xs text-[color:var(--alpha-mute)] mt-1">Received {new Date(active.created_at).toLocaleString()} · ID {active.id.slice(0, 8)}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {active.status !== "contacted" && <button onClick={() => updateStatus(active.id, "contacted")} className="alpha-btn-ghost text-xs py-2 px-3" data-testid="admin-mark-contacted">Mark contacted</button>}
                  {active.status !== "read" && <button onClick={() => updateStatus(active.id, "read")} className="alpha-btn-ghost text-xs py-2 px-3" data-testid="admin-mark-read">Mark read</button>}
                  {active.status !== "converted" && <button onClick={() => updateStatus(active.id, "converted")} className="alpha-btn-green text-xs py-2 px-3" data-testid="admin-mark-converted"><CheckCircle2 className="w-3.5 h-3.5" /> Converted</button>}
                  {active.status !== "resolved" && <button onClick={() => updateStatus(active.id, "resolved")} className="alpha-btn-primary text-xs py-2 px-3" data-testid="admin-mark-resolved"><CheckCircle2 className="w-3.5 h-3.5" /> Resolve</button>}
                </div>
              </div>
              {(active.data.email || active.data.phone) && (
                <div className="mt-4 flex flex-wrap gap-4 text-sm">
                  {active.data.email && <a href={`mailto:${active.data.email}`} className="flex items-center gap-2 text-[color:var(--alpha-purple-700)] font-semibold"><Mail className="w-4 h-4" /> {active.data.email}</a>}
                  {active.data.phone && <a href={`tel:${active.data.phone}`} className="flex items-center gap-2 text-[color:var(--alpha-purple-700)] font-semibold"><Phone className="w-4 h-4" /> {active.data.phone}</a>}
                </div>
              )}
              <table className="mt-5 w-full text-sm">
                <tbody>
                  {Object.entries(active.data).map(([k, v]) => (
                    <tr key={k} className="border-b border-[color:var(--alpha-line)]/70">
                      <td className="py-2 pr-4 font-semibold capitalize text-[color:var(--alpha-mute)] w-1/3">{k.replace(/_/g, " ")}</td>
                      <td className="py-2">{Array.isArray(v) ? v.join(", ") : v ? String(v) : <span className="text-[color:var(--alpha-mute)]">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default function AdminRoutes() {
  const authed = !!getAdminToken();
  return (
    <Routes>
      <Route index element={authed ? <Navigate to="/admin/inbox" replace /> : <LoginView />} />
      <Route path="inbox" element={authed ? <InboxView /> : <Navigate to="/admin" replace />} />
    </Routes>
  );
}
